import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useProfileState } from '@/contexts/ProfileContext';
import { useAuth } from '@/contexts/AuthContext';
import { analysis as analysisApi } from '@/lib/api';
import { localizedSearchTitle } from '@/lib/utils';
import { downloadCoverLetterPdf, emailCoverLetterAsPdf } from '@/lib/coverLetterPdf';
import { CoverLetterErrorBoundary } from '@/components/CoverLetterErrorBoundary';
import { trackEvent, EVENTS, showQuickWinToast } from '@/lib/analytics';

const ROLE_FILTERS = [
  { id: 'expected', label: 'Expected' },
  { id: 'adjacent', label: 'Adjacent' },
  { id: 'surprise', label: 'Stretch' },
  { id: 'all', label: 'All' },
];

// Human-readable pivot-mode labels + one-liner for the banner + the per-mode
// role-filter labels. This is what makes the pivot type VISIBLE in the UI
// — before this, level-up and shift-sideways produced role cards that
// looked identical because the mode was hidden.
const PIVOT_MODE_META = {
  'level-up': {
    label: 'Level up',
    banner: 'You chose a LEVEL-UP pivot — roles below climb ONE tier from where you are today.',
    filterLabels: { expected: 'One step up', adjacent: 'Sideways +1', surprise: 'Two-step leap' },
  },
  'shift-sideways': {
    label: 'Shift sideways',
    banner: "You chose a SHIFT-SIDEWAYS pivot — every role holds your current seniority; only the LANE (function, domain, or context) moves.",
    filterLabels: { expected: 'Same tier · new lane', adjacent: 'Bolder lane move', surprise: 'Boldest sideways bet' },
  },
  portfolio: {
    label: 'Portfolio',
    banner: 'You chose a PORTFOLIO pivot — these are engagement archetypes (advisory, fractional, board), not full-time jobs.',
    filterLabels: { expected: 'Highest-signal engagement', adjacent: 'Alternative engagement', surprise: 'Bolder engagement' },
  },
  return: {
    label: 'Return',
    banner: 'You chose a RETURN pivot — roles below re-enter your field at your PRIOR level, not entry-level.',
    filterLabels: { expected: 'Prior-level re-entry', adjacent: 'On-ramp bridge', surprise: 'Bold re-entry play' },
  },
  'enter-from-industry': {
    label: 'Enter from industry',
    banner: 'You chose to ENTER SUSTAINABILITY from another industry — roles below leverage your prior function as the bridge, at your prior-career seniority.',
    filterLabels: { expected: 'Direct crossover', adjacent: 'Alternative crossover', surprise: 'Bold crossover' },
  },
  'enter-from-academia': {
    label: 'Enter from academia',
    banner: 'You chose to ENTER SUSTAINABILITY from academia — roles below translate your research into industry impact.',
    filterLabels: { expected: 'Direct crossover', adjacent: 'Alternative crossover', surprise: 'Bold crossover' },
  },
  'recent-graduate': {
    label: 'Recent graduate',
    banner: 'You chose RECENT-GRADUATE entry — first roles that build the right sustainability foundation.',
    filterLabels: { expected: 'First-role fit', adjacent: 'Alternative first role', surprise: 'Bold first move' },
  },
};

// Fallback used when the LLM/schema returns a role.type_reason we can't parse
// cleanly — we still surface *something* explanatory instead of nothing.
const MOVE_CHIP_FALLBACKS = {
  'level-up': 'One rung up',
  'shift-sideways': 'Same tier · different lane',
  portfolio: 'Engagement, not a job',
  return: 'Re-entry at prior level',
  'enter-from-industry': 'Crossover from your industry',
  'enter-from-academia': 'Crossover from academia',
  'recent-graduate': 'First role fit',
};

export default function RoleExplorer() {
  const { analysis, savedProfile, pivotAnalyses, switchPivot, exploreMarket, activeMarketKey, markets } = useProfileState();
  const navigate = useNavigate();
  const c1 = analysis?.call1 || {};
  const [filter, setFilter] = useState('expected');
  const [pivotBusy, setPivotBusy] = useState(false);

  // Resolve the user's active pivot mode from their saved profile — this drives
  // the banner + role-card labels so the "different for level-up vs sideways"
  // difference is finally VISIBLE.
  const pivotType = (savedProfile?.pivot_type || 'level-up').trim();
  const pivotMeta = PIVOT_MODE_META[pivotType] || PIVOT_MODE_META['level-up'];

  const roles = useMemo(() => {
    const arr = c1.roles || [];
    return filter === 'all' ? arr : arr.filter(r => r.type === filter);
  }, [c1, filter]);

  // Which pivots does the user already have a saved analysis for on the CURRENT
  // market? These render as "instant switch" pills; unexplored pivots render as
  // "explore" pills that trigger a fresh analysis when tapped.
  const activeMk = activeMarketKey || '';
  const savedPivotTypes = useMemo(() => {
    const out = new Set();
    for (const entry of (pivotAnalyses || [])) {
      if ((entry.markets || []).some((m) => (m.market_key || '') === activeMk)) {
        out.add(entry.pivot_type);
      }
    }
    return out;
  }, [pivotAnalyses, activeMk]);

  // The 3 primary pivot types users pick from (return removed per product feedback).
  // Cross-over cohorts remain hidden unless the user is already on one.
  const PIVOT_CHOICES = ['level-up', 'shift-sideways', 'portfolio'];
  const extraPivots = ['enter-from-industry', 'enter-from-academia', 'recent-graduate']
    .filter((p) => savedPivotTypes.has(p) || p === pivotType);
  const pivotChoiceList = [...new Set([...PIVOT_CHOICES, ...extraPivots, pivotType])];

  const handleSwitchPivot = async (nextPivot) => {
    if (nextPivot === pivotType || pivotBusy) return;
    setPivotBusy(true);
    try {
      const res = await switchPivot(nextPivot);
      if (res?.needsAnalysis) {
        // No cache yet for this pivot on the current market — kick off a fresh
        // analysis on the CURRENT market (which might be a non-home market
        // like "Mexico"). We pass the current market's target_market string
        // explicitly so the backend saves the new (pivot, market) analysis
        // under the correct market_key — previously we always passed '' which
        // forced the run onto the home market and left non-home markets
        // stuck on the old pivot's roles.
        try {
          const currentMarketDoc = (markets || []).find((m) => m.market_key === activeMarketKey);
          const target_market_for_run = activeMarketKey
            ? (currentMarketDoc?.target_market || '')  // non-home: use the market's display string
            : '';                                       // home: '' is fine
          await exploreMarket({
            target_market: target_market_for_run,
            work_authorized_target: activeMarketKey
              ? !!currentMarketDoc?.work_authorized_target
              : !!savedProfile?.work_authorized_target,
            pivot_type: nextPivot, // pin the pivot so the fresh analysis is saved with the correct pivot_type
          });
        } catch (err) {
          const detail = err?.response?.data?.detail || err?.message;
          window.alert(detail || 'Could not start the analysis for this pivot. Please try again.');
        }
      }
    } finally {
      setPivotBusy(false);
    }
  };

  // Pick the best-fit role for the salary benchmark: highest match_pct among expected roles,
  // fall back to the first expected, then to the first role overall.
  const benchmarkRole = useMemo(() => {
    const all = c1.roles || [];
    const expected = all.filter(r => r.type === 'expected');
    const pool = expected.length ? expected : all;
    if (!pool.length) return null;
    return [...pool].sort((a, b) => (b.match_pct || 0) - (a.match_pct || 0))[0];
  }, [c1]);

  return (
    <div>
      <h1 className="text-[30px] font-semibold text-sd-black mb-2 heading-tight">Role Explorer</h1>
      <p className="text-sd-mid-gray text-[14.5px] mb-4">
        Five specific roles — two expected, two adjacent, and one stretch. Shape reflects your{' '}
        <span className="font-semibold text-sd-black">{pivotMeta.label}</span> pivot type.
      </p>

      {/* Pivot mode banner — makes the user's active mode explicit + shows
          which role-card interpretation is being applied. Data-testid lets us
          verify in tests that mode selection actually reaches the render. */}
      <div
        className="mb-4 rounded-xl border border-sd-light-gray bg-[#F6FAF6] p-4"
        data-testid={`pivot-mode-banner-${pivotType}`}
      >
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-sd-primary text-white flex items-center justify-center shrink-0 mt-0.5">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <path d="M8 3H5a2 2 0 0 0-2 2v3"/><path d="M21 8V5a2 2 0 0 0-2-2h-3"/><path d="M3 16v3a2 2 0 0 0 2 2h3"/><path d="M16 21h3a2 2 0 0 0 2-2v-3"/>
            </svg>
          </div>
          <div className="flex-1">
            <div className="flex items-baseline gap-2">
              <span className="text-[10.5px] font-semibold tracking-[0.09em] uppercase text-sd-primary">Active pivot</span>
              <span className="text-[11px] text-sd-mid-gray">{pivotMeta.label}</span>
            </div>
            <p className="text-[13.5px] text-sd-dark-gray leading-relaxed mt-1">{pivotMeta.banner}</p>
          </div>
        </div>

        {/* PivotSwitcher pill row — one pill per pivot type. Saved pivots
            switch instantly (no re-run); unexplored ones kick off a fresh
            analysis with a small elapsed-time chip visible via the global
            "In progress" poller. */}
        <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="pivot-switcher">
          <span className="text-[10.5px] font-semibold tracking-[0.09em] uppercase text-sd-mid-gray mr-1">Switch pivot</span>
          {pivotChoiceList.map((pt) => {
            const meta = PIVOT_MODE_META[pt] || { label: pt.replace(/-/g, ' ') };
            const isActive = pt === pivotType;
            const isSaved = savedPivotTypes.has(pt);
            return (
              <button
                key={pt}
                type="button"
                onClick={() => handleSwitchPivot(pt)}
                disabled={isActive || pivotBusy}
                className={
                  'text-[12px] font-medium px-3 py-1.5 rounded-pill border transition-colors ' +
                  (isActive
                    ? 'bg-sd-primary text-white border-sd-primary cursor-default'
                    : isSaved
                      ? 'bg-white text-sd-black border-sd-light-gray hover:border-sd-primary hover:text-sd-primary'
                      : 'bg-white text-sd-mid-gray border-dashed border-sd-light-gray hover:border-sd-primary hover:text-sd-primary')
                }
                data-testid={`pivot-switch-${pt}${isSaved ? '-saved' : '-new'}`}
                title={
                  isActive ? 'This is your current pivot view'
                    : isSaved ? 'Instant — you already have this saved'
                    : 'No saved analysis yet — will run a fresh one (~60-90s)'
                }
              >
                {isActive && (
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="inline-block mr-1 align-[-1px]">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                )}
                {meta.label}
                {!isActive && isSaved && (
                  <span className="ml-1.5 text-[10px] uppercase tracking-[0.06em] text-sd-primary">Saved</span>
                )}
                {!isActive && !isSaved && (
                  <span className="ml-1.5 text-[10px] uppercase tracking-[0.06em] text-sd-mid-gray">New</span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Explore other markets — Option B card at top of Role Explorer */}
      <ExploreMarketsCard homeLocation={savedProfile?.location || ''} currentPivot={pivotType} />

      {/* Salary Benchmark — Part 5a. Green-gradient banner card */}
      {benchmarkRole && <SalaryBenchmarkCard role={benchmarkRole} />}

      {/* Roles list */}
      <div className="mb-7">
        <div className="flex flex-wrap gap-2 mb-4">
          {ROLE_FILTERS.map(f => (
            <button key={f.id} className={`sd-chip ${filter === f.id ? 'sd-chip-on' : ''}`} onClick={() => setFilter(f.id)} data-testid={`role-filter-${f.id}`}>
              {f.label}
            </button>
          ))}
        </div>
        <div className="space-y-3">
          {roles.map((r, i) => <RoleCard key={i} role={r} />)}
          {roles.length === 0 && (
            <div className="sd-card p-6 text-center text-sd-mid-gray text-[13.5px]">No roles in this filter.</div>
          )}
        </div>
      </div>

      {/* CTA banner → JD Analyzer */}
      <section
        className="rounded-card p-8 mt-7 text-white cursor-pointer transition-all hover:translate-y-[-2px]"
        style={{ background: 'linear-gradient(135deg, #0A0A0A 0%, #1C2A1C 100%)' }}
        onClick={() => navigate('/dashboard/jd')}
        data-testid="jd-cta-banner"
      >
        <div className="flex items-center justify-between gap-6 flex-wrap">
          <div className="flex-1 min-w-[280px]">
            <h2 className="text-[22px] font-semibold heading-tight mb-2">Ready to apply? Test against a real posting.</h2>
            <p className="text-[#B8C0B8] text-[14.5px] leading-relaxed max-w-[560px]">
              Paste any sustainability job description and we'll score the fit, prep your interview answers, and provide LinkedIn recommendations that support your search.
            </p>
          </div>
          <button
            className="sd-btn-primary inline-flex items-center gap-2 !py-3 !px-5 !text-[14px] shrink-0"
            onClick={(e) => { e.stopPropagation(); navigate('/dashboard/jd'); }}
            data-testid="jd-cta-button"
          >
            Open JD Analyzer
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
            </svg>
          </button>
        </div>
      </section>
    </div>
  );
}

function SalaryBenchmarkCard({ role }) {
  const primarySalary = role.salary_ranges?.[0];
  if (!primarySalary?.range) return null;
  const scopeParts = [role.title, role.sector].filter(Boolean).join(' · ');

  return (
    <div className="sd-time-banner sd-time-banner--split mb-6" data-testid="salary-benchmark-card">
      <div className="sd-time-banner-main">
        <div className="sd-time-banner-lbl">Salary Benchmark · {scopeParts}</div>
        <p className="sd-time-banner-desc">{primarySalary.geo || role.sector}</p>
      </div>
      <div className="sd-time-banner__side">
        <div className="sd-time-banner-val" data-testid="salary-benchmark-range">{primarySalary.range}</div>
        <div className="sd-time-banner__side-sub">Base range</div>
      </div>
    </div>
  );
}

function RoleCard({ role }) {
  const [open, setOpen] = useState(false);
  const [coverLetterOpen, setCoverLetterOpen] = useState(false);
  const { savedProfile, markQuickWin } = useProfileState();

  // Fire "role expanded" event + auto-tick the Quick Win the first time the
  // user opens any role card. Both are best-effort.
  const doExpand = React.useCallback(() => {
    setOpen(true);
    try {
      trackEvent(EVENTS.role_card_expanded, {
        role_type: String(role.type || '').slice(0, 20),
      });
      markQuickWin('role_expanded').then((res) => {
        if (res && res.newly_completed) {
          showQuickWinToast('Explore your role paths');
        }
      }).catch(() => {});
    } catch (_e) { /* no-op */ }
  }, [role.type, markQuickWin]);

  const tone = role.type === 'expected' ? '#4CD964' : role.type === 'adjacent' ? '#D4943A' : '#7C5BD9';
  const toneBg = role.type === 'expected' ? '#E9FAEC' : role.type === 'adjacent' ? '#FBF1DE' : '#ECE6FB';

  // Pivot-mode-aware label for this card. The LLM writes `type_reason` for
  // each role (e.g. "FUNCTION SHIFT" for sideways, "+1 tier" for level-up).
  // We show that in a small chip so the user can SEE why this role fits their
  // chosen pivot mode — before this, the card just said "expected" and the
  // sideways vs. level-up distinction was invisible.
  const pivotType = (savedProfile?.pivot_type || 'level-up').trim();
  const modeMeta = PIVOT_MODE_META[pivotType] || PIVOT_MODE_META['level-up'];
  const rawTypeReason = (role.type_reason || '').trim();
  // Extract a short chip label (≤6 words). If type_reason starts with a
  // recognized prefix like "FUNCTION SHIFT:" we use that; otherwise fall back
  // to the mode-appropriate filter label.
  const modeChipLabel = (() => {
    if (rawTypeReason) {
      // Prefer a leading uppercase phrase before ':' or '—'
      const m = rawTypeReason.match(/^([A-Z][A-Z0-9 \-\/&]{2,50})(?:[:—]|$)/);
      if (m) return m[1].trim();
      // Otherwise use first 6 words
      const words = rawTypeReason.split(/\s+/).slice(0, 6).join(' ');
      if (words.length > 4) return words.replace(/[.,;:]$/, '');
    }
    return modeMeta.filterLabels[role.type] || MOVE_CHIP_FALLBACKS[pivotType] || 'Move';
  })();

  // Parse "City, Country" from the user's saved profile location. Fall back gracefully.
  const rawLoc = (savedProfile?.location || '').trim();
  const locParts = rawLoc.split(',').map(s => s.trim()).filter(Boolean);
  const userCity = locParts[0] || null;
  const userCountry = (() => {
    if (locParts.length < 2) return null;
    const last = locParts[locParts.length - 1];
    // Normalise common short forms for LinkedIn's search
    if (/^uk$/i.test(last)) return 'United Kingdom';
    if (/^us(a)?$/i.test(last)) return 'United States';
    return last;
  })();

  const searchUrl = (loc) => {
    // Strip the "— Industry" suffix so LinkedIn searches the clean role title,
    // then translate the title into the target country's language when we know
    // it (Fix 3). "Director of Sustainability" searched in Mexico → uses
    // "Director de Sustentabilidad" so LinkedIn actually returns the local
    // pipeline. All UI stays in English; only the outbound search keyword
    // changes.
    const localized = localizedSearchTitle(role.title, loc) || 'sustainability';
    const params = new URLSearchParams({ keywords: localized });
    if (loc) params.set('location', loc);
    return `https://www.linkedin.com/jobs/search/?${params.toString()}`;
  };

  const primarySalary = role.salary_ranges?.[0];

  return (
    <div className="sd-card overflow-hidden">
      {/* Row 1 — Title + badges (clickable to toggle) */}
      <button className="w-full p-5 text-left" onClick={() => (open ? setOpen(false) : doExpand())} data-testid={`role-card-${(role.title || '').slice(0, 16)}`}>
        <div className="flex items-start justify-between gap-5">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="text-[11px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-pill" style={{ background: toneBg, color: tone }}>{role.type}</span>
              {/* Pivot-mode chip — the visible signal that a shift-sideways/portfolio pivot is being honored */}
              <span
                className="text-[10.5px] uppercase tracking-[0.06em] font-semibold px-2 py-0.5 rounded-pill bg-sd-black/[0.06] text-sd-black/80"
                data-testid={`role-mode-chip-${pivotType}`}
                title={rawTypeReason || undefined}
              >
                {modeChipLabel}
              </span>
              <span className="text-[12px] text-sd-mid-gray">{role.sector}</span>
            </div>
            <h3 className="text-[18px] font-semibold text-sd-black">{role.title}</h3>
            {/* Row 2 — Why this fits (paragraph). Always visible so the collapsed card still communicates the value */}
            <p className="text-sd-dark-gray text-[13.5px] mt-1.5 leading-relaxed">{role.why}</p>
            {/* Full pivot-mode rationale — shown when it adds detail beyond the short chip */}
            {rawTypeReason && rawTypeReason.length > modeChipLabel.length + 4 && (
              <p className="text-sd-mid-gray text-[12px] mt-1.5 italic leading-relaxed">
                <span className="not-italic font-semibold text-sd-dark-gray/80">Why this fits your {modeMeta.label.toLowerCase()} pivot: </span>
                {rawTypeReason}
              </p>
            )}
          </div>
          <div className="text-right shrink-0">
            <div className="text-[28px] font-semibold text-sd-primary leading-none">{Math.round(role.match_pct || 0)}%</div>
            <div className="text-[10px] uppercase tracking-wider text-sd-mid-gray mt-1">Match</div>
          </div>
        </div>
      </button>

      {/* Show details toggle only when collapsed */}
      {!open && (
        <div className="px-5 pb-4 border-t border-sd-light-gray pt-3">
          <button
            type="button"
            className="sd-btn-secondary inline-flex items-center gap-2 !py-2.5 !px-4 !text-[13px]"
            onClick={doExpand}
            data-testid="role-show-details"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 12 15 18 9" />
            </svg>
            Show details
          </button>
        </div>
      )}

      {open && (
        <div className="px-5 pb-5 animate-fade-in" data-testid="role-card-expanded">
          <div className="border-t border-sd-light-gray pt-5 space-y-5">

            {/* Row 3 — Salary range (dark card) */}
            {primarySalary?.range && (
              <div className="rounded-[12px] p-6 text-white" style={{ background: '#1C1C1E' }}>
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-white font-semibold">Salary range</div>
                    {primarySalary.geo && <div className="text-[13px] text-white mt-1 opacity-90">{primarySalary.geo}</div>}
                  </div>
                  <div className="text-[28px] font-semibold font-mono leading-none" style={{ color: '#4CD964' }}>{primarySalary.range}</div>
                </div>
                {(primarySalary.bonus || primarySalary.source) && (
                  <div className="mt-4 pt-4 border-t" style={{ borderColor: '#2C2C2E' }}>
                    {primarySalary.bonus && <div className="text-[12px] text-[#B8BABE] leading-relaxed">{primarySalary.bonus}</div>}
                    {primarySalary.source && <div className="text-[12px] text-[#8E9096] mt-1"><span className="uppercase tracking-wider text-[10px] mr-1">Source:</span> {primarySalary.source}</div>}
                  </div>
                )}
              </div>
            )}

            {/* Row 4 — You have | You're missing (side-by-side) */}
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <div className="text-[11px] uppercase tracking-wider text-sd-mid-gray font-semibold mb-2">I have</div>
                <div className="flex flex-wrap gap-1.5">
                  {(role.skills_present || []).map((s, i) => (
                    <span key={i} className="text-[12px] font-medium px-2.5 py-1 rounded-pill" style={{ background: 'rgba(76, 217, 100, 0.12)', color: '#1F4D2A' }}>{s}</span>
                  ))}
                  {(role.skills_present || []).length === 0 && <span className="text-[12px] text-sd-mid-gray italic">—</span>}
                </div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wider text-sd-mid-gray font-semibold mb-2">I'm missing</div>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {(role.skills_gap || []).map((s, i) => (
                    <span key={i} className="text-[12px] font-medium px-2.5 py-1 rounded-pill" style={{ background: 'rgba(201, 64, 64, 0.12)', color: '#8A2F2F' }}>{s}</span>
                  ))}
                  {(role.skills_gap || []).length === 0 && <span className="text-[12px] text-sd-mid-gray italic">—</span>}
                </div>
                {role.key_gap && (
                  <div className="text-[13px] leading-relaxed" style={{ color: '#C94040' }}>
                    <strong>Key gap:</strong> {role.key_gap}
                  </div>
                )}
              </div>
            </div>

            {/* Row 5 — Market Signals */}
            {role.companies_hiring?.length > 0 && (
              <div>
                <div className="text-[11px] uppercase tracking-wider text-sd-mid-gray font-semibold mb-2">Market signals</div>
                <div className="grid sm:grid-cols-2 gap-2">
                  {role.companies_hiring.map((c, i) => (
                    <div key={i} className="p-3 rounded-[10px] bg-sd-cream">
                      <div className="text-[13.5px] font-semibold text-sd-black">{c.name}</div>
                      <div className="text-[12px] text-sd-mid-gray mt-0.5">{c.signal}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Row 6 — Two action buttons */}
            <div className="flex flex-wrap gap-2 pt-1">
              <a
                href={searchUrl(userCountry || undefined)}
                target="_blank"
                rel="noreferrer"
                className="sd-btn-primary inline-flex items-center gap-2 !py-2.5 !px-4 !text-[13px]"
                data-testid="role-search-national"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                </svg>
                Search nationally
              </a>
              {userCity && (
                <a
                  href={searchUrl(rawLoc || userCity)}
                  target="_blank"
                  rel="noreferrer"
                  className="sd-btn-secondary inline-flex items-center gap-2 !py-2.5 !px-4 !text-[13px]"
                  data-testid="role-search-local"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
                  </svg>
                  Near me ({userCity})
                </a>
              )}
              <button
                type="button"
                onClick={() => setCoverLetterOpen(true)}
                className="sd-btn-secondary inline-flex items-center gap-2 !py-2.5 !px-4 !text-[13px]"
                data-testid="role-cover-letter-btn"
                aria-label={`Draft a cover letter for ${role.title}`}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                </svg>
                Draft cover letter
              </button>
              <button
                type="button"
                className="sd-btn-secondary inline-flex items-center gap-2 !py-2.5 !px-4 !text-[13px] ml-auto"
                onClick={() => setOpen(false)}
                data-testid="role-collapse"
              >
                Show less
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
                  style={{ transform: 'rotate(180deg)' }}>
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {coverLetterOpen && (
        <CoverLetterModal role={role} onClose={() => setCoverLetterOpen(false)} />
      )}
    </div>
  );
}


/* ============================================================
   CoverLetterModal — generates and displays a first-draft cover letter for a
   specific shortlisted role. On-demand: one Claude call per open. User can
   copy to clipboard or download as .txt.
   ============================================================ */
export function CoverLetterModal(props) {
  return (
    <CoverLetterErrorBoundary onClose={props.onClose} roleTitle={props.role?.title}>
      <CoverLetterModalInner {...props} />
    </CoverLetterErrorBoundary>
  );
}

function CoverLetterModalInner({ role, onClose, initialDraft = null, onSaved = null }) {
  const { savedProfile, activeMarketKey } = useProfileState();
  const { user } = useAuth();
  const [tone, setTone] = useState(initialDraft?.tone || 'formal');
  const [toneHint, setToneHint] = useState(initialDraft?.tone_hint || '');
  const [showToneInput, setShowToneInput] = useState((initialDraft?.tone === 'custom') && !!initialDraft?.tone_hint);
  const [loading, setLoading] = useState(!initialDraft);
  const [error, setError] = useState(null);
  const [letter, setLetter] = useState(initialDraft ? {
    letter: initialDraft.body,
    opener: initialDraft.opener,
    closer: initialDraft.closer,
    tone: initialDraft.tone,
    word_count: initialDraft.word_count,
  } : null);
  const [copied, setCopied] = useState(false);
  const [saveState, setSaveState] = useState('idle'); // idle | saving | saved | error
  // Editable buffers — seeded from the latest generation. The user can freely
  // tweak these; Copy/Download always use these values, not the raw response.
  const [editBody, setEditBody] = useState(initialDraft?.body || '');
  const [editOpener, setEditOpener] = useState(initialDraft?.opener || '');
  const [editCloser, setEditCloser] = useState(initialDraft?.closer || '');
  const [dirty, setDirty] = useState(false);

  const generate = useCallback(async (targetTone, targetHint) => {
    const useTone = (targetTone || tone || 'formal').toLowerCase();
    const useHint = (typeof targetHint === 'string' ? targetHint : toneHint) || '';
    setLoading(true); setError(null); setLetter(null); setDirty(false);
    try {
      const resp = await analysisApi.coverLetter({
        market_key: activeMarketKey || undefined,
        tone: useTone,
        tone_hint: useHint,
        role: {
          title: role.title,
          sector: role.sector,
          why: role.why,
          skills_present: role.skills_present,
          skills_gap: role.skills_gap,
          key_gap: role.key_gap,
        },
      });
      setLetter(resp);
      setEditBody(resp.letter || '');
      setEditOpener(resp.opener || 'Dear Hiring Manager,');
      setEditCloser(resp.closer || 'With appreciation,');
    } catch (e) {
      console.error('[CoverLetterModal] generation failed:', {
        status: e?.response?.status,
        detail: e?.response?.data?.detail,
        roleTitle: role?.title,
        tone: useTone,
        hasHint: !!useHint,
      });
      setError(e?.response?.data?.detail || e?.message || 'Could not generate the draft. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [role, activeMarketKey, tone, toneHint]);

  React.useEffect(() => {
    // Skip generation when reloading an existing saved draft.
    if (!initialDraft) generate('formal');
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  // Persist the current in-memory draft to the user's history. Called on the
  // explicit commit actions (Copy / Download) — never on tone flips.
  // Dep array includes every value read inside so React re-binds on change
  // (previously `toneHint` and `wordCount` were referenced but not listed,
  // causing a stale-closure risk when the user typed a custom tone hint and
  // hit Copy quickly afterwards).
  // MUST be declared BEFORE `persist` — it sits in persist's dependency
  // array, which is evaluated at render time. Declaring it after caused a
  // temporal-dead-zone ReferenceError that crashed the whole app on open.
  const wordCount = useMemo(() => {
    const w = (editBody || '').trim().split(/\s+/).filter(Boolean).length;
    return w;
  }, [editBody]);

  const persist = React.useCallback(async () => {
    if (!editBody || !editBody.trim()) return;
    setSaveState('saving');
    try {
      const resp = await analysisApi.saveCoverLetter({
        role_title: role.title || 'Untitled role',
        role_sector: role.sector || '',
        tone,
        tone_hint: toneHint || '',
        market_key: activeMarketKey || '',
        opener: editOpener,
        body: editBody,
        closer: editCloser,
        word_count: wordCount,
        role_snapshot: {
          why: role.why,
          skills_present: role.skills_present,
          skills_gap: role.skills_gap,
          key_gap: role.key_gap,
        },
      });
      setSaveState('saved');
      if (onSaved && resp?.letter) onSaved(resp.letter);
      setTimeout(() => setSaveState('idle'), 2400);
    } catch (err) {
      console.warn('[CoverLetterModal] persist failed:', err?.response?.data || err?.message || err);
      setSaveState('error');
      setTimeout(() => setSaveState('idle'), 2400);
    }
  }, [editBody, editOpener, editCloser, tone, toneHint, activeMarketKey, role, wordCount, onSaved]);

  const switchTone = (next) => {
    if (next === tone || loading) return;
    // If the user has edits, ask once before nuking them.
    if (dirty && !window.confirm('You have unsaved edits. Switching tone will replace them with a fresh draft. Continue?')) {
      return;
    }
    setTone(next);
    if (next === 'custom') {
      // Expand the free-text field; only regenerate once the user submits
      setShowToneInput(true);
      return;
    }
    setShowToneInput(false);
    generate(next);
  };

  const applyCustomTone = () => {
    const hint = toneHint.trim();
    if (!hint || hint.length < 3) return;
    if (dirty && !window.confirm('You have unsaved edits. Applying a new tone will replace them. Continue?')) {
      return;
    }
    setTone('custom');
    generate('custom', hint);
  };

  const fullText = `${editOpener || 'Dear Hiring Manager,'}\n\n${editBody}\n\n${editCloser || 'With appreciation,'}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(fullText);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
      // Save on explicit commit action — spec 1a
      persist();
    } catch (err) {
      console.warn('[CoverLetterModal] clipboard.writeText failed:', err?.message || err);
      setError('Could not copy — select the text manually.');
    }
  };

  const download = () => {
    const blob = new Blob([fullText], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const safeTitle = (role.title || 'role').replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '').toLowerCase();
    a.download = `cover-letter-${safeTitle}-${tone}.txt`;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 400);
    // Save on explicit commit action — spec 1a
    persist();
  };

  // Compose a "letter object" that the PDF helper understands. We mirror the
  // shape saved to history so PDF + saved-record content stay consistent, and
  // enrich with a `candidate` block so the PDF opens with a proper cover
  // sheet (name, title, contact) recruiters can identify at a glance.
  const letterForExport = () => ({
    role_title: role.title || 'Cover letter',
    role_sector: role.sector || '',
    tone,
    tone_hint: toneHint,
    opener: editOpener,
    body: editBody,
    closer: editCloser,
    word_count: wordCount,
    candidate: {
      name: savedProfile?.name || user?.name || '',
      title: savedProfile?.title || '',
      email: user?.email || '',
      location: savedProfile?.location || '',
      linkedin: savedProfile?.linkedin_url || '',
    },
  });

  const downloadPdf = () => {
    downloadCoverLetterPdf(letterForExport());
    persist();
  };

  // Open the user's own email client with the letter pre-filled. mailto: can
  // NOT carry attachments (browser/OS constraint), so we (1) auto-download a
  // print-ready PDF, and (2) prime the email body with a clear "attach the
  // PDF from your Downloads" instruction plus the plain text as a fallback.
  const email = () => {
    emailCoverLetterAsPdf(letterForExport());
    // Also persist — the user has clearly committed to this draft.
    persist();
  };

  // Auto-resize the letter textarea to fit its content (avoids inner scrollbars
  // inside the modal, which itself scrolls).
  const bodyRef = React.useRef(null);
  React.useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 1200) + 'px';
  }, [editBody, loading]);

  const TONE_CHIPS = [
    { key: 'formal', label: 'Formal', hint: 'Traditional, precise, reserved' },
    { key: 'warm',   label: 'Warm',   hint: 'Human, conversational, personal' },
    { key: 'bold',   label: 'Bold',   hint: 'Direct, confident, forward-leaning' },
  ];

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-4 py-8 animate-fade-in"
      onClick={onClose}
      data-testid="cover-letter-modal"
    >
      <div
        className="bg-white rounded-card shadow-card-deep max-w-[720px] w-full max-h-[92vh] overflow-hidden flex flex-col animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-5 border-b border-sd-light-gray flex items-start justify-between gap-4 shrink-0">
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-[0.14em] text-sd-mid-gray font-semibold">Cover letter · First draft</div>
            <div className="text-[19px] font-semibold text-sd-black mt-0.5 leading-tight truncate">{role.title}</div>
            <div className="text-[12.5px] text-sd-mid-gray mt-0.5">
              Tune the tone, tweak the words, then copy or download.
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full hover:bg-sd-cream flex items-center justify-center text-sd-mid-gray hover:text-sd-black transition-colors shrink-0"
            aria-label="Close"
            data-testid="cover-letter-close"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Tone chips */}
        <div className="px-6 py-3 border-b border-sd-light-gray bg-sd-ivory shrink-0" role="group" aria-label="Tone selector">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[11px] uppercase tracking-[0.14em] text-sd-mid-gray font-semibold mr-1">Tone</span>
            {TONE_CHIPS.map((t) => {
              const active = tone === t.key;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => switchTone(t.key)}
                  disabled={loading}
                  className={`px-3.5 py-1.5 rounded-full text-[13px] font-medium transition-all border ${
                    active
                      ? 'bg-sd-black text-white border-sd-black shadow-sm'
                      : 'bg-white text-sd-dark-gray border-sd-light-gray hover:border-sd-mid-gray hover:text-sd-black'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                  data-testid={`cover-letter-tone-${t.key}`}
                  aria-pressed={active}
                  title={t.hint}
                >
                  {t.label}
                </button>
              );
            })}
            <button
              type="button"
              onClick={() => switchTone('custom')}
              disabled={loading}
              className={`px-3.5 py-1.5 rounded-full text-[13px] font-medium transition-all border inline-flex items-center gap-1.5 ${
                tone === 'custom'
                  ? 'bg-sd-black text-white border-sd-black shadow-sm'
                  : 'bg-white text-sd-dark-gray border-dashed border-sd-mid-gray hover:border-sd-black hover:text-sd-black'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
              data-testid="cover-letter-tone-custom"
              aria-pressed={tone === 'custom'}
              title="Describe the voice you want in your own words"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
              Custom
            </button>
            {dirty && !loading && (
              <span className="text-[11.5px] text-sd-mid-gray ml-auto italic" data-testid="cover-letter-dirty-hint">
                Edited — changing tone will replace your edits
              </span>
            )}
          </div>

          {showToneInput && (
            <div className="mt-3 flex items-center gap-2" data-testid="cover-letter-tone-custom-row">
              <div className="flex-1 relative">
                <input
                  type="text"
                  value={toneHint}
                  onChange={(e) => setToneHint(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); applyCustomTone(); } }}
                  placeholder='e.g. "witty but professional", "warm McKinsey manager", "no jargon, plain English"'
                  className="w-full bg-white border border-sd-light-gray focus:border-sd-primary focus:ring-1 focus:ring-sd-primary/30 outline-none text-[13.5px] text-sd-black py-2 px-3 pr-16 rounded-lg transition-colors"
                  data-testid="cover-letter-tone-hint-input"
                  maxLength={200}
                  autoFocus
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10.5px] text-sd-mid-gray tabular-nums pointer-events-none">
                  {toneHint.length}/200
                </span>
              </div>
              <button
                type="button"
                onClick={applyCustomTone}
                disabled={loading || toneHint.trim().length < 3}
                className="sd-btn-primary !py-2 !px-3.5 !text-[13px] shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="cover-letter-tone-hint-apply"
              >
                Apply
              </button>
            </div>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {loading && (
            <div className="py-12 text-center" data-testid="cover-letter-loading">
              <div className="inline-block w-10 h-10 border-[3px] border-sd-light-gray border-t-sd-primary rounded-full animate-spin mb-4" />
              <div className="text-[14px] font-medium text-sd-dark-gray">
                Drafting your <span className="capitalize">{tone === 'custom' ? 'custom-tone' : tone}</span> cover letter…
              </div>
              <div className="text-[12.5px] text-sd-mid-gray mt-1">Tuning tone, evidence and closing line · ~15 seconds</div>
            </div>
          )}
          {!loading && error && (
            <div className="rounded-card border-l-4 border-sd-error bg-[#FDF2F2] text-[#76313B] p-4 text-[14px]" data-testid="cover-letter-error">
              {error}
              <button
                type="button"
                onClick={() => generate()}
                className="sd-btn-primary !py-2 !px-3 !text-[13px] ml-3"
                data-testid="cover-letter-retry"
              >
                Try again
              </button>
            </div>
          )}
          {!loading && !error && letter && (
            <div className="space-y-3" data-testid="cover-letter-body">
              <label className="block">
                <span className="text-[11px] uppercase tracking-[0.14em] text-sd-mid-gray font-semibold">Opener</span>
                <input
                  type="text"
                  value={editOpener}
                  onChange={(e) => { setEditOpener(e.target.value); setDirty(true); }}
                  className="mt-1 w-full bg-transparent border-0 border-b border-sd-light-gray focus:border-sd-primary outline-none text-[15px] font-medium text-sd-black py-1.5 transition-colors"
                  data-testid="cover-letter-opener-input"
                />
              </label>
              <label className="block">
                <span className="text-[11px] uppercase tracking-[0.14em] text-sd-mid-gray font-semibold">Body</span>
                <textarea
                  ref={bodyRef}
                  value={editBody}
                  onChange={(e) => { setEditBody(e.target.value); setDirty(true); }}
                  className="mt-1 w-full bg-transparent border border-sd-light-gray rounded-lg focus:border-sd-primary focus:ring-1 focus:ring-sd-primary/30 outline-none text-[14.5px] leading-[1.7] text-sd-dark-gray p-3 resize-none font-sans transition-colors"
                  rows={12}
                  data-testid="cover-letter-body-input"
                  spellCheck="true"
                />
              </label>
              <label className="block">
                <span className="text-[11px] uppercase tracking-[0.14em] text-sd-mid-gray font-semibold">Sign-off</span>
                <input
                  type="text"
                  value={editCloser}
                  onChange={(e) => { setEditCloser(e.target.value); setDirty(true); }}
                  className="mt-1 w-full bg-transparent border-0 border-b border-sd-light-gray focus:border-sd-primary outline-none text-[15px] font-medium text-sd-black py-1.5 transition-colors"
                  data-testid="cover-letter-closer-input"
                />
              </label>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="px-6 py-4 border-t border-sd-light-gray flex items-center justify-between gap-3 shrink-0 bg-sd-ivory">
          <div className="text-[12px] text-sd-mid-gray">
            {letter ? (
              <>
                {wordCount} words · <span className="capitalize">{tone === 'custom' ? 'custom' : tone}</span> tone
                {tone === 'custom' && toneHint && (
                  <span className="ml-1 text-sd-mid-gray italic truncate inline-block max-w-[200px] align-bottom" title={toneHint}>
                    · "{toneHint}"
                  </span>
                )}
                {dirty && <span className="ml-1 text-sd-black">· edited</span>}
                {saveState === 'saved' && (
                  <span className="ml-2 inline-flex items-center gap-1 text-sd-primary-dark font-medium" data-testid="cover-letter-saved-flag">
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                    Saved to history
                  </span>
                )}
                {saveState === 'saving' && <span className="ml-2 text-sd-mid-gray">Saving…</span>}
                {saveState === 'error' && <span className="ml-2 text-sd-error">Couldn't save</span>}
              </>
            ) : ' '}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => generate()}
              disabled={loading}
              className="sd-btn-secondary inline-flex items-center gap-1.5 !py-2 !px-3 !text-[13px] disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="cover-letter-regen"
              aria-label="Regenerate draft"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <polyline points="23 4 23 10 17 10"/>
                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
              </svg>
              Regenerate
            </button>
            <button
              type="button"
              onClick={copy}
              disabled={loading || !letter}
              className="sd-btn-secondary inline-flex items-center gap-1.5 !py-2 !px-3 !text-[13px] disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="cover-letter-copy"
            >
              {copied ? (
                <>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#4CD964" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  Copied
                </>
              ) : (
                <>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                  </svg>
                  Copy
                </>
              )}
            </button>
            <button
              type="button"
              onClick={email}
              disabled={loading || !letter}
              className="sd-btn-secondary inline-flex items-center gap-1.5 !py-2 !px-3 !text-[13px] disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="cover-letter-email"
              aria-label="Email as PDF attachment"
              title="Downloads a print-ready PDF and opens your email client with an attach reminder"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                <polyline points="22,6 12,13 2,6"/>
              </svg>
              Email as PDF
            </button>
            <button
              type="button"
              onClick={downloadPdf}
              disabled={loading || !letter}
              className="sd-btn-primary inline-flex items-center gap-1.5 !py-2 !px-3 !text-[13px] disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="cover-letter-download-pdf"
              title="Print-ready PDF (A4, typographic)"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <path d="M9 13h1M9 17h1"/>
                <path d="M13 13h2M13 17h2"/>
              </svg>
              Download .pdf
            </button>
            <button
              type="button"
              onClick={download}
              disabled={loading || !letter}
              className="sd-btn-secondary inline-flex items-center gap-1.5 !py-2 !px-3 !text-[13px] disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="cover-letter-download"
              title="Plain text .txt"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              .txt
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
function ExploreMarketsCard({ homeLocation, currentPivot }) {
  const {
    activeMarketKey,
    markets,
    marketSwitching,
    switchToMarket,
    exploreMarket,
    deleteMarket,
    pendingMarketJob,
    pendingProgressPct,
  } = useProfileState();

  const [showModal, setShowModal] = useState(false);

  // Derived — is a market analysis currently in flight?
  const pending = pendingMarketJob;
  const pendingLabel = pendingMarketJob?.target_market || '';
  const progressPct = pendingProgressPct;

  // ---------- Live countdown ---------------------------------------------
  // Ticks every second while a market job is pending. Remaining time is
  // computed from a mode-aware ETA (150s refresh / 210s full run), clamped by
  // both wall-clock elapsed AND the current progress bar. If the progress
  // bar suggests we're closer to done than the clock, trust the bar (backend
  // milestones are the source of truth for real completion). Once we blow
  // past the estimate we quietly say "Wrapping up…" instead of a negative.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!pending) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [pending?.job_id]);

  const countdown = useMemo(() => {
    if (!pending) return null;
    const startedAt = pending.started_at || now;
    const elapsedSec = Math.max(0, Math.round((now - startedAt) / 1000));
    const etaTotal = pending.mode === 'refresh' ? 150 : 210; // seconds
    // Blend two estimators — take the smaller so we don't overpromise.
    const byClock = etaTotal - elapsedSec;
    const byProgress = progressPct > 0
      ? Math.round(etaTotal * (100 - progressPct) / 100)
      : etaTotal;
    const remaining = Math.min(byClock, byProgress);
    if (remaining <= 5 || progressPct >= 99) return 'Wrapping up…';
    if (remaining <= 0) return 'Wrapping up…';
    const m = Math.floor(remaining / 60);
    const s = remaining % 60;
    if (m === 0) return `~${s}s remaining`;
    return `~${m}m ${s.toString().padStart(2, '0')}s remaining`;
  }, [pending, now, progressPct]);

  const homeMarket = markets.find((m) => m.is_home);
  const nonHomeMarkets = markets.filter((m) => !m.is_home);

  const activeMarketDoc = markets.find((m) => m.market_key === activeMarketKey);
  const activeLabel = activeMarketDoc?.is_home
    ? (homeLocation || 'Home')
    : (activeMarketDoc?.target_market || homeLocation || 'Home');

  const onExplore = async (target_market, work_authorized_target, pivot_type) => {
    setShowModal(false);
    try {
      await exploreMarket({ target_market, work_authorized_target, pivot_type });
      // Polling is handled globally by ProfileContext — nothing else to do here.
    } catch (e) {
      // Surface the actual backend reason instead of a generic message —
      // "profile not found", "another market is still running", 401 auth, etc.
      // are things the user can act on. Only fall back to the generic phrase
      // when we can't extract anything useful from the response.
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      let msg;
      if (status === 401) {
        msg = 'Your session has expired. Please sign in again to add a market.';
      } else if (status === 429) {
        msg = 'Another market analysis is still running — wait for it to finish, then try again.';
      } else if (detail && typeof detail === 'string') {
        msg = detail;
      } else if (e?.message && e.message !== 'Network Error') {
        msg = e.message;
      } else {
        msg = 'Could not start the market analysis. Please check your connection and try again.';
      }
      window.alert(msg);
    }
  };

  const onSwitch = (mk) => {
    if (marketSwitching || pending) return;
    switchToMarket(mk);
  };

  return (
    <section className="sd-market-card" data-testid="explore-markets-card">
      <div className="sd-market-card__body">
        <div className="sd-market-card__eyebrow">
          <span aria-hidden>🌍</span>
          <span>Looking to move?</span>
        </div>
        <h3 className="sd-market-card__title">
          These roles are calibrated for <span className="sd-market-card__loc" data-testid="market-calibration-label">{activeLabel || homeLocation || 'your home market'}</span>.
        </h3>
        <p className="sd-market-card__sub">
          Enter another city, country, or region to re-cast the salaries, hiring signals, network and events for that market. We keep up to 3 explored markets, so you can flip between them without waiting.
        </p>

        <div className="sd-market-chips" data-testid="market-chips">
          {homeMarket && (
            <button
              type="button"
              className={`sd-market-chip ${activeMarketKey === '' ? 'is-active' : ''}`}
              onClick={() => onSwitch('')}
              disabled={!!pending}
              data-testid="market-chip-home"
              title={`Your home market (${homeLocation || 'not set'})`}
            >
              <span aria-hidden>📍</span>
              <span>{homeLocation || 'Home'}</span>
              <span className="sd-market-chip__tag">Home</span>
            </button>
          )}
          {nonHomeMarkets.map((m) => (
            <div key={m.market_key} className={`sd-market-chip ${activeMarketKey === m.market_key ? 'is-active' : ''}`} data-testid={`market-chip-${m.market_key}`}>
              <button
                type="button"
                className="sd-market-chip__label"
                onClick={() => onSwitch(m.market_key)}
                disabled={!!pending}
                title={m.work_authorized_target ? 'You have work authorization here' : 'No work authorization — visa needed'}
              >
                <span aria-hidden>🌐</span>
                <span>{m.target_market}</span>
                {!m.work_authorized_target && <span className="sd-market-chip__visa">Visa needed</span>}
              </button>
              <button
                type="button"
                className="sd-market-chip__remove"
                onClick={(e) => { e.stopPropagation(); deleteMarket(m.market_key); }}
                aria-label={`Remove ${m.target_market}`}
                title="Remove this market"
                data-testid={`market-chip-remove-${m.market_key}`}
                disabled={!!pending}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          ))}
        </div>

        <div className="sd-market-actions">
          <button
            type="button"
            className="sd-market-card__cta"
            onClick={() => setShowModal(true)}
            disabled={!!pending || nonHomeMarkets.length >= 3}
            data-testid="explore-market-open"
          >
            {nonHomeMarkets.length >= 3 ? 'Remove one to explore another market' : 'Explore another market'}
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
            </svg>
          </button>
        </div>

        {activeMarketKey && (
          <div className="sd-market-active-note" data-testid="market-active-note">
            <span>Currently exploring: <strong>{activeLabel}</strong>{activeMarketDoc && !activeMarketDoc.work_authorized_target ? ' · visa needed' : ''}</span>
            <button type="button" onClick={() => onSwitch('')} className="sd-market-reset" data-testid="market-reset-home">
              Reset to my home market
            </button>
          </div>
        )}

        {pending && (
          <div className="sd-market-progress" role="status" aria-live="polite" data-testid="market-progress" data-mode={pending?.mode || 'full'}>
            {pending?.mode === 'refresh' ? (
              <>
                <div className="sd-market-progress__badge sd-market-progress__badge--refresh" data-testid="market-progress-badge">
                  <span className="sd-market-progress__badge-dot" />
                  Light refresh · reusing your cached profile
                </div>
                <div className="sd-market-progress__label">
                  Recalibrating for <strong>{pendingLabel}</strong>… only market-specific fields (roles, salaries, hiring, events, network) are being re-cast. Your saved skills, gaps and milestones stay put.
                </div>
                <div className="sd-market-progress__eta">
                  Usually 2 – 3 minutes.
                  {countdown && (
                    <span className="sd-market-progress__countdown" data-testid="market-progress-countdown"> · {countdown}</span>
                  )}
                </div>
              </>
            ) : (
              <>
                <div className="sd-market-progress__badge sd-market-progress__badge--full" data-testid="market-progress-badge">
                  <span className="sd-market-progress__badge-dot" />
                  Full analysis · building a fresh profile for this market
                </div>
                <div className="sd-market-progress__label">
                  Analyzing <strong>{pendingLabel}</strong>… the complete profile is being re-cast for this market.
                </div>
                <div className="sd-market-progress__eta">
                  Usually 3 – 4 minutes.
                  {countdown && (
                    <span className="sd-market-progress__countdown" data-testid="market-progress-countdown"> · {countdown}</span>
                  )}
                </div>
              </>
            )}
            <div className="sd-market-progress__bar" aria-hidden>
              <div className="sd-market-progress__fill" style={{ width: `${progressPct}%` }} />
              {/* Phase ticks — three milestones the user watches the fill sweep past.
                  Positions are fixed, colour lights up once passed. */}
              <span className={`sd-market-progress__tick ${progressPct >= 33 ? 'is-hit' : ''}`} style={{ left: '33%' }} aria-hidden />
              <span className={`sd-market-progress__tick ${progressPct >= 66 ? 'is-hit' : ''}`} style={{ left: '66%' }} aria-hidden />
              <span className={`sd-market-progress__tick ${progressPct >= 99 ? 'is-hit' : ''}`} style={{ left: 'calc(100% - 3px)' }} aria-hidden />
            </div>
            <div className="sd-market-progress__phases" aria-hidden>
              <span className={progressPct >= 6 ? 'is-active' : ''}>Roles</span>
              <span className={progressPct >= 33 ? 'is-active' : ''}>Salaries</span>
              <span className={progressPct >= 66 ? 'is-active' : ''}>Events</span>
              <span className={progressPct >= 99 ? 'is-active' : ''}>Done</span>
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <ExploreMarketModal onCancel={() => setShowModal(false)} onSubmit={onExplore} homeLocation={homeLocation} currentPivot={currentPivot} />
      )}
    </section>
  );
}

function ExploreMarketModal({ onCancel, onSubmit, homeLocation, currentPivot }) {
  const [value, setValue] = useState('');
  const [auth, setAuth] = useState(false);
  // Default to the user's current pivot (level-up / shift-sideways / portfolio).
  // Return / crossover cohorts are intentionally NOT offered here — the market
  // exploration flow is for the 3 primary pivots per product feedback.
  const [pivot, setPivot] = useState(
    ['level-up', 'shift-sideways', 'portfolio'].includes(currentPivot) ? currentPivot : 'level-up'
  );
  const canSubmit = value.trim().length >= 2;
  const submit = useCallback(() => {
    if (!canSubmit) return;
    onSubmit(value.trim(), auth, pivot);
  }, [canSubmit, value, auth, pivot, onSubmit]);
  const PIVOT_OPTIONS = [
    { id: 'level-up', label: 'Level up', hint: 'Climb one rung' },
    { id: 'shift-sideways', label: 'Shift sideways', hint: 'Same rung, new lane' },
    { id: 'portfolio', label: 'Portfolio', hint: 'Advisory, fractional, board' },
  ];
  return (
    <div className="sd-market-modal-backdrop" role="dialog" aria-modal="true" onClick={onCancel} data-testid="explore-market-modal">
      <div className="sd-market-modal" onClick={(e) => e.stopPropagation()}>
        <div className="sd-market-modal__head">
          <h3>Explore another market</h3>
          <button type="button" onClick={onCancel} aria-label="Close" className="sd-market-modal__x" data-testid="explore-market-close">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <p className="sd-market-modal__sub">
          We'll re-run the analysis for this market. Salaries, hiring signals, network suggestions, events and regulatory context will all be re-cast. Your home market ({homeLocation || 'not set'}) stays cached.
        </p>
        <label className="sd-market-modal__label" htmlFor="explore-market-input">City, country, or region</label>
        <input
          id="explore-market-input"
          className="sd-input"
          type="text"
          placeholder="e.g., New York, US · London, UK · Berlin, DE · Remote-US"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && canSubmit) submit(); }}
          autoFocus
          data-testid="explore-market-input"
        />
        {/* Pivot picker — decoupled from the profile default so users can
            explore e.g. NYC as Portfolio without their Level-up home view
            being disturbed. */}
        <label className="sd-market-modal__label" style={{ marginTop: 16 }}>Scenario</label>
        <div className="grid grid-cols-3 gap-2 mt-1" data-testid="explore-market-pivot">
          {PIVOT_OPTIONS.map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => setPivot(opt.id)}
              className={
                'text-left px-3 py-2 rounded-[10px] border transition-colors ' +
                (pivot === opt.id
                  ? 'bg-sd-primary text-white border-sd-primary'
                  : 'bg-white text-sd-black border-sd-light-gray hover:border-sd-primary')
              }
              data-testid={`explore-market-pivot-${opt.id}`}
            >
              <div className="text-[12.5px] font-semibold">{opt.label}</div>
              <div className={'text-[10.5px] mt-0.5 ' + (pivot === opt.id ? 'text-white/85' : 'text-sd-mid-gray')}>{opt.hint}</div>
            </button>
          ))}
        </div>
        <label className="sd-market-modal__auth" style={{ marginTop: 16 }}>
          <input
            type="checkbox"
            checked={auth}
            onChange={(e) => setAuth(e.target.checked)}
            data-testid="explore-market-workauth"
          />
          <span>I have work authorization in this market (citizen, permanent resident, or existing work permit).</span>
        </label>
        <p className="sd-market-modal__auth-hint">
          If unchecked, we'll honestly reduce role match scores by 10-15 points and add sponsorship guidance in your blind spots and network.
        </p>
        <div className="sd-market-modal__actions">
          <button type="button" onClick={onCancel} className="sd-market-modal__cancel" data-testid="explore-market-cancel">Cancel</button>
          <button
            type="button"
            onClick={submit}
            disabled={!canSubmit}
            className="sd-market-modal__submit"
            data-testid="explore-market-submit"
          >
            Explore this market
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
